import React, { useEffect, useState } from 'react';
import { useNavigate, Outlet, Link } from 'react-router-dom';
import { clientAuthApi, clientPortalApi } from '../lib/http';
import logo from '../assets/logo.png';
import UserProfileButton from './client/UserProfileButton';

interface ClientLayoutProps {
  children?: React.ReactNode;
}

const ClientLayout: React.FC<ClientLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check session and fetch unread count
    const checkSession = async () => {
      try {
        await clientAuthApi.session();

        // Fetch unread notifications count
        const response = await clientPortalApi.notifications.list({ pageSize: 1 });
        setUnreadCount(response.data.unreadCount);

        setLoading(false);
      } catch (error: any) {
        console.error('Session check failed:', error);

        // Only redirect to login on authentication errors (401, 403)
        // Don't redirect on rate limit (429) or network errors
        if (error.response?.status === 401 || error.response?.status === 403) {
          navigate('/client/login');
        } else {
          // For other errors (429, network, etc), just set loading to false
          setLoading(false);
        }
      }
    };

    checkSession();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await clientAuthApi.signout();
      sessionStorage.removeItem('sessionRole');
      sessionStorage.removeItem('client');
      navigate('/auth/select');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50/80 to-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-full border-2 border-purple-200 border-t-purple-600 animate-spin" />
          <span className="text-sm text-gray-500">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50/50 via-white to-white">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg sticky top-0 z-50 border-b border-gray-100/80">
        <div className="px-4 sm:px-6 lg:px-8 safe-area-inset-top max-w-3xl lg:max-w-5xl mx-auto">
          <div className="flex justify-between items-center h-14">
            {/* Logo/Home */}
            <Link to="/u" className="flex items-center ml-4">
              <img src={logo} alt="BookDay" className="h-8 w-auto" />
            </Link>

            {/* Right side - Icons + Profile (visible on all screens) */}
            <div className="flex items-center gap-1">
              {/* My Bookings */}
              <Link
                to="/u/bookings"
                className="flex items-center justify-center w-10 h-10 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-xl transition-all active:scale-95 cursor-pointer"
                aria-label="My Bookings"
              >
                <svg
                  className="w-[22px] h-[22px]"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.75}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </Link>

              {/* Notifications */}
              <Link
                to="/u/notifications"
                className="relative flex items-center justify-center w-10 h-10 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-xl transition-all active:scale-95 cursor-pointer"
                aria-label="Notifications"
              >
                <svg
                  className="w-[22px] h-[22px]"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.75}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-gradient-to-r from-purple-600 to-pink-500 rounded-full shadow-sm">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </Link>

              {/* User Profile */}
              <UserProfileButton onLogout={handleLogout} />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 pt-4 pb-8 sm:px-6 lg:px-8 max-w-3xl lg:max-w-5xl mx-auto">
        {children || <Outlet />}
      </main>
    </div>
  );
};

export default ClientLayout;
